import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, PackageSearch } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import { inboundReceiveRmaCountCheckedMessage } from "@integrator-src/api/shipping-inbound/shipping-inbound-receive-rma-devices";
import {
  fetchReceivePurchaseOrderQuickSearch,
  fetchReceiveRmaQuickSearch,
  fetchReceiveTransferOrderQuickSearch,
  matchInboundReceiveRmaDevices,
  type ReceivePurchaseOrderQuickSearchRow,
  type ReceiveTransferOrderQuickSearchRow,
} from "./inboundApi";
import {
  INBOUND_RECEIVE_ROUTE_DOCK_PICK_OPTIONS,
  inboundReceiveRouteForDockPicker,
  inboundReceiveRouteLabel,
  type InboundReceiveRoute,
} from "./inboundReceiveRoute";

function purchaseOrderLabel(row: ReceivePurchaseOrderQuickSearchRow): string {
  return row.number?.trim() || `PO #${String(row.id)}`;
}

function transferOrderLabel(row: ReceiveTransferOrderQuickSearchRow): string {
  return row.number?.trim() || `Transfer #${String(row.id)}`;
}

export function InboundReceiveArrivalRouteModal(props: {
  open: boolean;
  scan: string;
  packageNo: number;
  vendorName?: string | null;
  initialRoute?: InboundReceiveRoute | null;
  pending?: boolean;
  onApply: (input: {
    receive_route: InboundReceiveRoute;
    purchaseOrderId?: number;
    transferOrderId?: number;
    rmaId?: number;
    deviceSerials?: string[];
  }) => void;
  onCancel: () => void;
}): JSX.Element | null {
  const [route, setRoute] = useState<InboundReceiveRoute | null>(
    inboundReceiveRouteForDockPicker(props.initialRoute ?? null),
  );
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedRmaId, setSelectedRmaId] = useState<number | null>(null);
  const [deviceDraft, setDeviceDraft] = useState("");
  const [scannedSerials, setScannedSerials] = useState<string[]>([]);
  const deviceScanRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!props.open) {
      setRoute(inboundReceiveRouteForDockPicker(props.initialRoute ?? null));
      setQuery("");
      setDebouncedQuery("");
      setSelectedRmaId(null);
      setDeviceDraft("");
      setScannedSerials([]);
      return;
    }
    setRoute(inboundReceiveRouteForDockPicker(props.initialRoute ?? null));
    setSelectedRmaId(null);
    setDeviceDraft("");
    setScannedSerials([]);
  }, [props.open, props.initialRoute]);

  useEffect(() => {
    if (!props.open) return;
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [props.open, query]);

  useEffect(() => {
    if (!props.open) return;
    deviceScanRef.current?.focus();
  }, [props.open]);

  const poSearchQuery = useQuery({
    queryKey: ["shipping", "inbound", "receive-po-search", debouncedQuery],
    queryFn: () => fetchReceivePurchaseOrderQuickSearch(debouncedQuery),
    enabled: props.open && route === "purchase_order" && debouncedQuery.length >= 2,
    staleTime: 15_000,
  });

  const rmaSearchQuery = useQuery({
    queryKey: ["shipping", "inbound", "receive-rma-search", debouncedQuery],
    queryFn: () => fetchReceiveRmaQuickSearch(debouncedQuery),
    enabled: props.open && route === "rma" && debouncedQuery.length >= 2,
    staleTime: 15_000,
  });

  const transferSearchQuery = useQuery({
    queryKey: ["shipping", "inbound", "receive-transfer-search", debouncedQuery],
    queryFn: () => fetchReceiveTransferOrderQuickSearch(debouncedQuery),
    enabled: props.open && route === "transfer_order" && debouncedQuery.length >= 2,
    staleTime: 15_000,
  });

  const poRows = useMemo(() => poSearchQuery.data ?? [], [poSearchQuery.data]);
  const rmaRows = useMemo(() => rmaSearchQuery.data ?? [], [rmaSearchQuery.data]);
  const transferRows = useMemo(() => transferSearchQuery.data ?? [], [transferSearchQuery.data]);
  const deviceMatchQuery = useQuery({
    queryKey: ["shipping", "inbound", "rma-device-match", scannedSerials],
    queryFn: () => matchInboundReceiveRmaDevices(scannedSerials),
    enabled: props.open && scannedSerials.length > 0,
  });
  const checkedMatch = deviceMatchQuery.data?.confirmed ?? null;
  const partialMatch = useMemo(() => {
    if (checkedMatch != null) return null;
    return deviceMatchQuery.data?.matches.find((row) => row.matchedSerials.length > 0) ?? null;
  }, [checkedMatch, deviceMatchQuery.data]);
  const vendor = props.vendorName?.trim() ?? "";

  function addDeviceSerial(raw: string) {
    const serial = raw.trim();
    if (serial === "") return;
    setScannedSerials((current) => (current.some((row) => row.toUpperCase() === serial.toUpperCase()) ? current : [...current, serial]));
    setDeviceDraft("");
  }

  useEffect(() => {
    if (checkedMatch == null) return;
    setRoute("rma");
    const rmaId = Number.parseInt(checkedMatch.rmaId, 10);
    if (Number.isSafeInteger(rmaId)) setSelectedRmaId(rmaId);
  }, [checkedMatch]);

  if (!props.open) return null;

  const needsOrderSearch = route === "purchase_order" || route === "transfer_order";
  const specialDesk = route === "rma" || route === "non_po_purchase";

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <div
        className="mt-4 w-full max-w-lg rounded-lg border border-slate-200 bg-white p-4 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">Route this arrival</h2>
        <p className="mt-1 text-sm text-slate-600">
          ARRIVED carton {String(props.packageNo)}
          {vendor !== "" ? ` (${vendor})` : ""}.{" "}
          {props.initialRoute === "rma"
            ? "Scan each device in this carton. When the count checks out, print a sticker and take it to the QC Analysis station."
            : "Scan a device from the carton if this might be a return. Otherwise choose how receiving should handle it."}
        </p>
        <div className="mt-4">
          <label className="text-sm font-medium text-slate-700" htmlFor="inbound-receive-rma-device-scan">
            Device from this carton
          </label>
          <Input
            id="inbound-receive-rma-device-scan"
            ref={deviceScanRef}
            value={deviceDraft}
            className="mt-1 min-h-11 font-mono text-base"
            placeholder="Scan a device label"
            disabled={props.pending}
            autoComplete="off"
            onChange={(event) => setDeviceDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              addDeviceSerial(deviceDraft);
            }}
          />
          {scannedSerials.length > 0 ? (
            <p className="mt-2 text-xs text-slate-600">
              Scanned {String(scannedSerials.length)} device{scannedSerials.length === 1 ? "" : "s"}.
            </p>
          ) : null}
          {deviceMatchQuery.isFetching ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Checking open RMAs…
            </p>
          ) : null}
          {checkedMatch != null ? (
            <p className="mt-2 text-sm font-medium text-emerald-800">
              {inboundReceiveRmaCountCheckedMessage(checkedMatch.rmaNumber)}
            </p>
          ) : partialMatch != null ? (
            <p className="mt-2 text-sm text-slate-700">
              {partialMatch.rmaNumber}: {String(partialMatch.matchedSerials.length)} of {String(partialMatch.expectedCount)} devices. Keep scanning.
            </p>
          ) : deviceMatchQuery.data != null && deviceMatchQuery.data.unmatchedSerials.length > 0 ? (
            <p className="mt-2 text-sm text-slate-600">That device is not on an open RMA.</p>
          ) : null}
        </div>
        <div className="mt-4 space-y-2">
          {INBOUND_RECEIVE_ROUTE_DOCK_PICK_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={props.pending}
              className={cn(
                "w-full rounded-lg border px-3 py-2 text-left",
                route === option.value
                  ? "border-sky-400 bg-sky-50"
                  : "border-slate-200 bg-slate-50 hover:border-sky-300 hover:bg-sky-50",
                props.pending ? "cursor-not-allowed opacity-60" : "",
              )}
              onClick={() => {
                setRoute(option.value);
                setQuery("");
                setDebouncedQuery("");
                setSelectedRmaId(null);
              }}
            >
              <span className="block text-sm font-semibold text-slate-900">{option.label}</span>
              <span className="block text-xs text-slate-600">{option.description}</span>
            </button>
          ))}
        </div>
        {route === "rma" ? (
          <div className="mt-4">
            <label className="text-sm font-medium text-slate-700" htmlFor="inbound-receive-route-rma-search">
              Customer RMA
            </label>
            <Input
              id="inbound-receive-route-rma-search"
              value={query}
              className="mt-1 min-h-11 font-mono text-base"
              placeholder="Type RMA number or customer"
              disabled={props.pending}
              autoComplete="off"
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className="mt-3 max-h-48 overflow-y-auto">
              {rmaSearchQuery.isFetching ? (
                <p className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Searching…
                </p>
              ) : null}
              <ul className="space-y-2">
                {rmaRows.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      disabled={props.pending}
                      className={cn(
                        "flex w-full flex-col gap-0.5 rounded-lg border px-3 py-3 text-left",
                        selectedRmaId === row.id
                          ? "border-sky-400 bg-sky-50"
                          : "border-slate-200 bg-slate-50 hover:border-sky-300 hover:bg-sky-50",
                        props.pending ? "cursor-not-allowed opacity-60" : "",
                      )}
                      onClick={() => setSelectedRmaId(row.id)}
                    >
                      <span className="text-base font-semibold text-slate-900">{row.rmaNumber}</span>
                      {(row.customerName?.trim() ?? "") !== "" || (row.complaint?.trim() ?? "") !== "" ? (
                        <span className="text-xs text-slate-600">
                          {[row.customerName?.trim(), row.complaint?.trim()].filter(Boolean).join(" · ")}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
        {needsOrderSearch && route != null ? (
          <div className="mt-4">
            <label className="text-sm font-medium text-slate-700" htmlFor="inbound-receive-route-order-search">
              {route === "purchase_order" ? "Purchase order" : "Transfer order"}
            </label>
            <Input
              id="inbound-receive-route-order-search"
              value={query}
              className="mt-1 min-h-11 font-mono text-base"
              placeholder="Type at least 2 characters"
              disabled={props.pending}
              autoComplete="off"
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className="mt-3 max-h-48 overflow-y-auto">
              {(route === "purchase_order" ? poSearchQuery : transferSearchQuery).isFetching ? (
                <p className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Searching…
                </p>
              ) : null}
              <ul className="space-y-2">
                {(route === "purchase_order" ? poRows : transferRows).map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      disabled={props.pending}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-left",
                        "hover:border-sky-300 hover:bg-sky-50",
                        props.pending ? "cursor-not-allowed opacity-60" : "",
                      )}
                      onClick={() => {
                        if (route == null) return;
                        props.onApply({
                          receive_route: route,
                          purchaseOrderId: route === "purchase_order" ? row.id : undefined,
                          transferOrderId: route === "transfer_order" ? row.id : undefined,
                        });
                      }}
                    >
                      <PackageSearch className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
                      <span className="text-base font-semibold text-slate-900">
                        {route === "purchase_order" ? purchaseOrderLabel(row as ReceivePurchaseOrderQuickSearchRow) : transferOrderLabel(row as ReceiveTransferOrderQuickSearchRow)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" disabled={props.pending} onClick={props.onCancel}>
            Cancel
          </Button>
          {checkedMatch != null ? (
            <Button
              type="button"
              disabled={props.pending}
              onClick={() => {
                const rmaId = Number.parseInt(checkedMatch.rmaId, 10);
                if (!Number.isSafeInteger(rmaId)) return;
                props.onApply({
                  receive_route: "rma",
                  rmaId,
                  deviceSerials: scannedSerials,
                });
              }}
            >
              Print sticker
            </Button>
          ) : specialDesk && route != null ? (
            <Button
              type="button"
              disabled={props.pending || (route === "rma" && selectedRmaId == null)}
              onClick={() => {
                if (route === "rma") {
                  if (selectedRmaId == null) return;
                  props.onApply({ receive_route: route, rmaId: selectedRmaId });
                  return;
                }
                props.onApply({ receive_route: route });
              }}
            >
              Open {inboundReceiveRouteLabel(route)} desk
            </Button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
